// Phase I (I-C, I3): applies the tenant's branding as theme tokens across the app and portal.
// Colours go through brandTokens(), which adjusts them for contrast in light and dark mode, so a
// pale accent never makes links unreadable. The favicon follows the tenant's upload.
import { useEffect } from 'react';
import { DEFAULT_BRAND, brandTokens, type BrandColours } from '@denwen-msp/shared';
import { useAuth } from '@/app/auth';
import { useBranding, useHostInfo } from './api';

const STYLE_ID = 'denwen-msp-brand-tokens';

/** The CSS for both schemes. Tailwind's --color-* are resolved at :root, so they are set too. */
export function brandCss(colours: Partial<BrandColours>): string {
  const block = (scheme: 'light' | 'dark') =>
    Object.entries(brandTokens(colours, scheme))
      .flatMap(([k, v]) => [`${k}:${v}`, `--color-${k.slice(2)}:${v}`])
      .join(';');
  return `:root{${block('light')}}.dark{${block('dark')}}`;
}

function setFavicon(href: string | null) {
  let link = document.querySelector<HTMLLinkElement>('link[rel="icon"][data-brand]');
  if (!href) {
    link?.remove();
    return;
  }
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    link.dataset.brand = '1';
    document.head.appendChild(link);
  }
  link.href = href;
}

/** Mount once near the root. Renders nothing. */
export function BrandTheme() {
  const { user } = useAuth();
  const host = useHostInfo();
  const branding = useBranding(Boolean(user));
  // Signed in: the tenant's own settings. Signed out: the host's tenant, if any.
  const source = user ? branding.data : host.data?.branding;
  // Unbranded tenants keep the stock theme (its dark-mode accent is tuned by hand).
  const branded = source && (source.primaryColor !== DEFAULT_BRAND.primaryColor || source.accentColor !== DEFAULT_BRAND.accentColor);
  const css = branded ? brandCss({ primaryColor: source.primaryColor, accentColor: source.accentColor }) : null;
  const favicon = source ? ('favicon' in source ? (source.favicon?.url ?? null) : source.faviconUrl) : null;

  useEffect(() => {
    let style = document.getElementById(STYLE_ID);
    if (!css) {
      style?.remove();
      return;
    }
    if (!style) {
      style = document.createElement('style');
      style.id = STYLE_ID;
      document.head.appendChild(style);
    }
    style.textContent = css;
  }, [css]);

  useEffect(() => setFavicon(favicon), [favicon]);
  return null;
}
